﻿import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const COLORS = ['#2e7d32', '#c62828'];
const JOB_COLORS = ['#2e7d32', '#c62828', '#ed6c02', '#1976d2', '#6d4c41'];
const formatNumber = n => (n ?? 0).toLocaleString('en-US');
const formatLastBackup = (v) => {
  if (!v) return 'N/A';
  try { const d = new Date(v); if (isNaN(d.getTime())) return 'N/A';
    const datePart = d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
    const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart}, ${timePart}`; }
    catch (catchError) {
        Sentry.captureException(catchError);
        return 'N/A';
    }
};
const formatDateTime = v => {
  try {
    const d = new Date(v);
    if (isNaN(d)) return '';
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (catchError) {
      Sentry.captureException(catchError);
    return '';
  }
};
const pct = (num, den) => den > 0 ? ((num / den) * 100).toFixed(1) + '%' : '0%';

const RubrikBackupSummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line

  async function fetchSummary() {
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/api/rubrik/backups-summary');
      if (!res.ok) throw new Error('Failed to fetch Rubrik backup summary');
      const json = await res.json();
      setData(json);
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setData(null);
    }
    finally { setLoading(false); }
  }

  const compliant = data?.compliantObjects ?? data?.successfulTasks ?? 0;
  const nonCompliant = data?.nonCompliantObjects ?? data?.failedObjects ?? 0;
  const complianceRate = data?.complianceRate != null ? data.complianceRate : (compliant + nonCompliant > 0 ? (compliant / (compliant + nonCompliant)) * 100 : 0);
  const restoreSuccessRate = data?.restoreSuccessRate ?? 0;
  const backupSuccessRate = data?.backupSuccessRate ?? 0;
  const deltaBackupRate = data?.deltaBackupSuccessRate ?? 0;
  const deltaRestoreRate = data?.deltaRestoreSuccessRate ?? 0;
  const previousWindow = data?.previousMonthWindow || data?.previousMonth || null;
  const prevRangeLabel = previousWindow ? new Date(previousWindow.start).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'Prev Month';
  const comparisonLabel = data?.comparisonLabel || 'Prior Month';
  const jobStatsAvailable = data?.jobStatsAvailable !== false;
  const failedJobs = data?.failedJobs || [];

  // Object-level (attempts-collapsed) backup protection metrics. A backup that failed and
  // then succeeded on a later attempt is reported as protected/recovered, not as a failure.
  const backupObjectsTotal = data?.backupObjectsTotal ?? 0;
  const backupObjectsProtected = data?.backupObjectsProtected ?? 0;
  const backupObjectsRecovered = data?.backupObjectsRecovered ?? data?.recoveredObjectsCount ?? 0;
  const backupObjectsPersistentlyFailing = data?.backupObjectsPersistentlyFailing ?? 0;
  const hasObjectLevelStats = data?.effectiveBackupSuccessRate != null && backupObjectsTotal > 0;
  const effectiveBackupRate = hasObjectLevelStats ? data.effectiveBackupSuccessRate : backupSuccessRate;

  // Calculate data protection health score
  const healthScore = useMemo(() => {
    if (!data) return null;
    
    // Compliance: 50% weight (most critical - is data actually protected?)
    const complianceScore = complianceRate;
    
    // Backup Success: 30% weight (are backups completing successfully?)
    // Use the object-level effective rate when available so that objects which failed
    // then recovered on a retry are correctly treated as protected.
    const backupScore = jobStatsAvailable && data.backupJobsTotal > 0
      ? (hasObjectLevelStats ? effectiveBackupRate : backupSuccessRate)
      : null;
    
    // Restore Success: 20% weight (can we actually recover data when needed?)
    const restoreScore = jobStatsAvailable && data.restoreJobsTotal > 0 ? restoreSuccessRate : null;
    
    // Calculate weighted score with normalization for missing metrics
    let totalWeight = 0.5; // compliance always counted
    let weightedSum = complianceScore * 0.5;
    
    if (backupScore != null) {
      totalWeight += 0.3;
      weightedSum += backupScore * 0.3;
    }
    
    if (restoreScore != null) {
      totalWeight += 0.2;
      weightedSum += restoreScore * 0.2;
    }
    
    if (totalWeight === 0) return null;
    
    const overall = weightedSum / totalWeight;
    return Math.round(overall);
  }, [data, complianceRate, backupSuccessRate, restoreSuccessRate, jobStatsAvailable, hasObjectLevelStats, effectiveBackupRate]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 95) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 90) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 80) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 70) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const complianceChartData = useMemo(() => [
    { name: 'Compliant', value: compliant },
    { name: 'Non-Compliant', value: nonCompliant }
  ], [compliant, nonCompliant]);
  const restoreChartData = useMemo(() => [ { name: 'Succeeded', value: data?.restoreJobsSucceeded || 0 }, { name: 'Failed', value: data?.restoreJobsFailed || 0 } ], [data]);
  const backupChartData = useMemo(() => [ { name: 'Succeeded', value: data?.backupJobsSucceeded || 0 }, { name: 'Failed', value: data?.backupJobsFailed || 0 } ], [data]);
  const statusBreakdownData = useMemo(() => {
    if (!data?.jobStatusBreakdown) {
      return [];
    }
    const entries = Object.entries(data.jobStatusBreakdown);
    const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
    return sortedEntries.map(([k, v]) => ({ status: k, count: v }));
  }, [data]);
  const backupObjectTypeRows = useMemo(() => { const stats = data?.backupObjectTypeStats || {}; return Object.entries(stats).map(([k,v])=>({ type:k, ...v, successRate: pct(v.succeeded, v.total)})).sort((a,b)=>b.total-a.total); }, [data]);
  const restoreObjectTypeRows = useMemo(() => { const stats = data?.restoreObjectTypeStats || {}; return Object.entries(stats).map(([k,v])=>({ type:k, ...v, successRate: pct(v.succeeded, v.total)})).sort((a,b)=>b.total-a.total); }, [data]);

  const statusColor = data?.status === 'Healthy' || data?.status === 'Success' ? '#2e7d32' : data?.status === 'Warning' ? '#ed6c02' : '#555';

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!data) return null;

  const tableBody = rows => (
    <div style={{ flex:1, minHeight:0, overflow:'auto' }}>
          <table className="data-table">
        <thead>
          <tr style={{ textAlign:'left', background:'#333', color:'#fff' }}>
            <th style={{ padding:'4px 6px', width:'34%' }}>Type</th>
            <th style={{ padding:'4px 6px', textAlign:'right' }}>Total</th>
            <th style={{ padding:'4px 6px', textAlign:'right' }}>Succ</th>
            <th style={{ padding:'4px 6px', textAlign:'right' }}>Fail</th>
            <th style={{ padding:'4px 6px', textAlign:'right' }}>Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.type}>
              <td style={{ padding:'3px 6px', fontWeight:500 }}>{r.type}</td>
              <td style={{ padding:'3px 6px', textAlign:'right' }}>{formatNumber(r.total)}</td>
              <td style={{ padding:'3px 6px', textAlign:'right', color:'#2e7d32' }}>{formatNumber(r.succeeded)}</td>
              <td style={{ padding:'3px 6px', textAlign:'right', color:r.failed? '#c62828':'#777' }}>{formatNumber(r.failed)}</td>
              <td style={{ padding:'3px 6px', textAlign:'right' }}>{r.successRate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const scoreInterp = getScoreInterpretation(healthScore);
  const totalObjects = compliant + nonCompliant;
  const hasRestoreFailures = jobStatsAvailable && data.restoreJobsFailed > 0;

  const llmProxyText = [
    `Data Protection Health Score: ${healthScore}/100`,
    `Compliance Rate: ${complianceRate.toFixed(1)}%`,
    `Backup Success (effective, latest attempt per object): ${effectiveBackupRate.toFixed(1)}%`,
    `Backup Success (raw attempts): ${backupSuccessRate.toFixed(1)}%`,
    `Objects recovered after retry: ${formatNumber(backupObjectsRecovered)}`,
    `Objects persistently failing: ${formatNumber(backupObjectsPersistentlyFailing)}`,
    `Restore Success: ${restoreSuccessRate.toFixed(1)}%`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper rubrik-backup-summary">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom:12 }}>Data Protection & Business Continuity</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Data Protection Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> <strong>{complianceRate.toFixed(0)}%</strong> of your {formatNumber(totalObjects)} Microsoft 365 objects are protected with valid backups. Backup jobs are <strong>{effectiveBackupRate.toFixed(0)}% successful</strong>{jobStatsAvailable && data.restoreJobsTotal > 0 && ` with ${restoreSuccessRate.toFixed(0)}% restore success rate`}.
              {hasObjectLevelStats && backupObjectsRecovered > 0 && <span style={{ color: '#7cb342' }}> {formatNumber(backupObjectsRecovered)} {backupObjectsRecovered === 1 ? 'object' : 'objects'} failed but recovered automatically on a retry.</span>}
              {complianceRate >= 95 && effectiveBackupRate >= 95 && ' Your data protection is excellent.'}
              {complianceRate < 95 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {nonCompliant > 0 && `${formatNumber(nonCompliant)} objects lack valid backups - data at risk.`}</span>}
              {hasObjectLevelStats && backupObjectsPersistentlyFailing > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {formatNumber(backupObjectsPersistentlyFailing)} {backupObjectsPersistentlyFailing === 1 ? 'object is' : 'objects are'} still failing after every attempt - investigate.</span>}
              {hasRestoreFailures && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Recent restore failures detected - recovery capability is compromised.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card" title="Percentage of objects with valid backup protection">
          <div className="metric-label">Data Compliance</div>
          <div className="metric-value" style={{ color: complianceRate >= 95 ? '#2e7d32' : complianceRate >= 90 ? '#ffa000' : '#d32f2f' }}>
            {complianceRate.toFixed(0)}%
          </div>
          <div className="metric-subtitle">
            {formatNumber(compliant)} / {formatNumber(totalObjects)} objects
          </div>
        </div>
        <div className="dashboard-card metric-card" title="Percentage of objects whose latest backup attempt succeeded. Objects that failed then recovered on a retry count as protected.">
          <div className="metric-label">Backup Success</div>
          <div className="metric-value" style={{ color: !jobStatsAvailable ? '#999' : effectiveBackupRate >= 95 ? '#2e7d32' : effectiveBackupRate >= 90 ? '#ffa000' : '#d32f2f' }}>
            {jobStatsAvailable ? `${effectiveBackupRate.toFixed(0)}%` : 'N/A'}
          </div>
          {jobStatsAvailable && (
            <div className="metric-subtitle">
              {hasObjectLevelStats
                ? <>{formatNumber(backupObjectsProtected)} / {formatNumber(backupObjectsTotal)} objects protected</>
                : <>{formatNumber(data.backupJobsSucceeded)} / {formatNumber(data.backupJobsTotal)} jobs</>}
              {deltaBackupRate !== 0 && (
                <span className="metric-delta" style={{ color: deltaBackupRate >= 0 ? 'green' : '#d32f2f', marginLeft: 4 }}>
                  {deltaBackupRate >= 0 ? '↑' : '↓'} {Math.abs(deltaBackupRate).toFixed(1)}%
                </span>
              )}
              {hasObjectLevelStats && backupObjectsRecovered > 0 && (
                <div style={{ color: '#7cb342', marginTop: 2 }}>
                  {formatNumber(backupObjectsRecovered)} recovered after retry
                </div>
              )}
            </div>
          )}
        </div>
        <div className="dashboard-card metric-card" title="Success rate of data recovery operations - validates backup integrity">
          <div className="metric-label">Restore Success</div>
          <div className="metric-value" style={{ color: !jobStatsAvailable || data.restoreJobsTotal === 0 ? '#999' : restoreSuccessRate >= 95 ? '#2e7d32' : restoreSuccessRate >= 90 ? '#ffa000' : '#d32f2f' }}>
            {jobStatsAvailable && data.restoreJobsTotal > 0 ? `${restoreSuccessRate.toFixed(0)}%` : 'N/A'}
          </div>
          {jobStatsAvailable && data.restoreJobsTotal > 0 && (
            <div className="metric-subtitle">
              {formatNumber(data.restoreJobsSucceeded)} / {formatNumber(data.restoreJobsTotal)} restores
              {deltaRestoreRate !== 0 && (
                <span className="metric-delta" style={{ color: deltaRestoreRate >= 0 ? 'green' : '#d32f2f', marginLeft: 4 }}>
                  {deltaRestoreRate >= 0 ? '↑' : '↓'} {Math.abs(deltaRestoreRate).toFixed(1)}%
                </span>
              )}
            </div>
          )}
        </div>
        <div className="dashboard-card metric-card" title="Total Microsoft 365 objects under Rubrik protection">
          <div className="metric-label">Protected Objects</div>
          <div className="metric-value">{formatNumber(data.protectedObjects)}</div>
          <div className="metric-subtitle">{data.objectTypes || 'N/A'} types</div>
        </div>
        <div className="dashboard-card metric-card metric-card--lg" title="Most recent successful backup completion time">
          <div className="metric-label">Last Backup</div>
          <div className="metric-value--text" style={{ fontWeight: 700, marginTop: 4, color: statusColor }}>{formatLastBackup(data.lastBackup)}</div>
          <div className="metric-subtitle" style={{ marginTop: 4 }}>Status: {data.status}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
            <h4 className="chart-title">Data Protection Coverage</h4>
            <div className="chart-subtitle">Objects with valid backups</div>
            <div className="chart-wrap--lg">
              <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={isPrintMode ? 200 : "100%"}>
                <PieChart>
                  <Pie data={complianceChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(d)=>`${d.name} (${d.value})`} isAnimationActive={!isPrintMode}>
                    {complianceChartData.map((e,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                  </Pie>
                  <Tooltip/><Legend/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        {jobStatsAvailable && (
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Backup Jobs ({prevRangeLabel})</h4>
            <div className="chart-subtitle">Job completion status</div>
            <div className="chart-wrap--lg">
              <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={isPrintMode ? 200 : "100%"}>
                <BarChart data={backupChartData} layout="vertical" margin={{ left:16, right:8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} />
                  <Tooltip />
                  <Bar dataKey="value" isAnimationActive={!isPrintMode}>{backupChartData.map((e,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Restore Operations + Job Status Distribution */}
      {jobStatsAvailable && (
        <div className="dashboard-grid compact">
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Restore Operations ({prevRangeLabel})</h4>
            <div className="chart-subtitle">Data recovery testing</div>
            <div className="chart-wrap--lg">
              <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={isPrintMode ? 200 : "100%"}>
                <BarChart data={restoreChartData} layout="vertical" margin={{ left:16, right:8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} />
                  <Tooltip />
                  <Bar dataKey="value" isAnimationActive={!isPrintMode}>{restoreChartData.map((e,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Job Status Distribution ({prevRangeLabel})</h4>
            <div className="chart-subtitle">Breakdown by completion status</div>
            <div className="chart-wrap--lg">
              {statusBreakdownData.length === 0 && <div style={{ fontSize:11, color:'#777' }}>No job data.</div>}
              {statusBreakdownData.length > 0 && (
                <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={isPrintMode ? 200 : "100%"}>
                  <BarChart data={statusBreakdownData} layout="vertical" margin={{ left:0, right:8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="status" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" isAnimationActive={!isPrintMode}>{statusBreakdownData.map((e,i)=><Cell key={i} fill={JOB_COLORS[i%JOB_COLORS.length]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Object Type Tables */}
      {jobStatsAvailable && (
        <div className="dashboard-grid compact rubrik-object-tables">
          <div className="dashboard-card chart-card rubrik-object-type-card rubrik-backup-type-full">
            <h4 className="chart-title">Backup by Object Type</h4>
            <div className="chart-subtitle">Performance by data type (Exchange, SharePoint, OneDrive, etc.)</div>
            {backupObjectTypeRows.length === 0 && <div style={{ fontSize:11, color:'#777' }}>No backup job detail.</div>}
            {backupObjectTypeRows.length > 0 && tableBody(backupObjectTypeRows)}
          </div>
          <div className="dashboard-card chart-card rubrik-object-type-card">
            <h4 className="chart-title">Restore by Object Type</h4>
            <div className="chart-subtitle">Recovery testing by data type</div>
            {restoreObjectTypeRows.length === 0 && <div style={{ fontSize:11, color:'#777' }}>No restore job detail.</div>}
            {restoreObjectTypeRows.length > 0 && tableBody(restoreObjectTypeRows)}
          </div>
        </div>
      )}

      {/* Persistently Failing Objects Detail */}
      {jobStatsAvailable && failedJobs.length > 0 && (
        <div className="dashboard-grid compact pdf-exclude" style={{ marginTop:4 }}>
          <div className="dashboard-card" style={{ marginBottom:0, padding:14, flex:'1 1 100%' }}>
            <h4 style={{ margin:'0 0 8px 0', fontSize:14 }}>Persistently Failing Objects ({prevRangeLabel})</h4>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
              Objects whose <strong>latest</strong> attempt failed and never recovered - investigate and remediate.
              {backupObjectsRecovered > 0 && ` ${formatNumber(backupObjectsRecovered)} other object${backupObjectsRecovered === 1 ? '' : 's'} failed earlier but recovered on a retry and ${backupObjectsRecovered === 1 ? 'is' : 'are'} excluded here.`}
            </div>
            <div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, tableLayout:'fixed' }}>
                <thead>
                  <tr style={{ background:'#fafafa', textAlign:'left' }}>
                    <th style={{ padding:'4px 6px', width:'90px' }}>Latest Attempt</th>
                    <th style={{ padding:'4px 6px', width:'70px' }}>Type</th>
                    <th style={{ padding:'4px 6px', width:'90px' }}>Obj Type</th>
                    <th style={{ padding:'4px 6px', width:'200px' }}>Object Name</th>
                    <th style={{ padding:'4px 6px', width:'60px', textAlign:'center' }}>Attempts</th>
                    <th style={{ padding:'4px 6px', width:'95px' }}>Last Success</th>
                    <th style={{ padding:'4px 6px' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {failedJobs.map(j => (
                    <tr key={j.id} style={{ borderTop:'1px solid #eee', verticalAlign:'top' }}>
                      <td style={{ padding:'3px 6px', whiteSpace:'nowrap' }}>{formatDateTime(j.startTime)}</td>
                      <td style={{ padding:'3px 6px' }}>{j.jobType}</td>
                      <td style={{ padding:'3px 6px' }}>{j.objectType || '-'}</td>
                      <td style={{ padding:'3px 6px', wordBreak:'break-word' }}>{j.objectName || '-'}</td>
                      <td style={{ padding:'3px 6px', textAlign:'center' }}>
                        {j.attempts != null
                          ? <span title={`${j.failedAttempts ?? j.attempts} failed of ${j.attempts}`}>{j.failedAttempts ?? j.attempts}/{j.attempts}</span>
                          : '-'}
                      </td>
                      <td style={{ padding:'3px 6px', whiteSpace:'nowrap', color: j.lastSuccess ? '#777' : '#c62828' }}>
                        {j.lastSuccess ? formatDateTime(j.lastSuccess) : 'Never'}
                      </td>
                      <td style={{ padding:'3px 6px', wordBreak:'break-word', whiteSpace:'pre-wrap' }}>{j.failureReason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box rubrik-metrics-explanation">
        <strong>Understanding Data Protection Metrics:</strong>
        <ul>
          <li><strong>Data Protection Health Score:</strong> Combines Compliance (50%), Backup Success (30%), and Restore Success (20%). Target is 95%+ for business continuity.</li>
          <li><strong>Data Compliance:</strong> Percentage of objects with valid backups. {'>'}95% is critical - anything less means data at risk if disaster strikes.</li>
          <li><strong>Backup Success:</strong> Measured per object using its <em>latest</em> attempt. An object that failed then succeeded on a later run counts as protected, so transient retries no longer inflate the failure rate.</li>
          <li><strong>Restore Success:</strong> Ability to actually recover data. The ultimate test - backups are worthless if you can't restore. Regular restore testing is essential.</li>
          <li><strong>RTO/RPO:</strong> Recovery Time Objective (how quickly you can recover) and Recovery Point Objective (how much data loss is acceptable). Your backup schedule determines RPO.</li>
          <li><strong>Persistently Failing Objects:</strong> Objects whose latest attempt failed and never recovered - these need immediate investigation. Common causes: permissions, API limits, object deletions, storage quota. Objects that recovered on a retry are tracked separately and excluded.</li>
        </ul>
        {nonCompliant > 0 && (
          <div className="action-callout">
            <strong>Action Required:</strong> {formatNumber(nonCompliant)} objects lack valid backups. Review non-compliant objects and ensure backup policies are properly assigned. This represents potential data loss in a disaster scenario.
          </div>
        )}
      </div>

      <div className="data-source">
        Data Source: Rubrik GraphQL (M365 snappable objects & activity logs). Reporting period: {prevRangeLabel}. Comparison: {comparisonLabel}. {data?.jobWindow?.type === 'last30Days' ? 'Note: Using last 30 days data (full month metrics unavailable).' : ''}
      </div>
    </div>
  );
};

export default RubrikBackupSummary;